import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// ⚖️ LEGAL REVIEW REMINDER ⚖️
// This Privacy Policy is a first-pass draft, NOT lawyer-reviewed.
// REQUIRED ACTION: Have a Canadian privacy lawyer review this policy
// BEFORE the app reaches ~500 active users OR before charging paying customers,
// whichever comes first. Estimated cost: CAD $300–800.
// Look for Canadian tech / privacy lawyers specializing in SaaS + AI products.
// See also: /app/memory/LEGAL_REVIEW_REMINDER.md

const LAST_UPDATED = 'May 14, 2026';
const COMPANY = 'Live in Vibrancy Coaching Ltd.';
const APP_NAME = 'Inner Work';
const CONTACT_EMAIL = 'client_care@liveinvibrancy.com';
const COMPANY_ADDRESS = '13920 117 Ave NW, Edmonton, Alberta T5M 3J4, Canada';
const GOVERNING_LAW = 'the Province of Alberta, Canada';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-sand relative overflow-hidden">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12" data-testid="privacy-page">
        <Link
          to="/login"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase mb-8"
          style={{ transition: 'color 0.2s ease' }}
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">{APP_NAME}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading mb-3">Privacy Policy</h1>
        <p className="text-text-muted font-sans text-xs tracking-wider mb-2">Last updated: {LAST_UPDATED}</p>
        <div className="mystical-divider my-8 max-w-[80px]" />

        <div className="prose-content space-y-8 font-sans text-base text-dark leading-relaxed">

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3 mt-2">1. Who We Are</h2>
            <p>
              {APP_NAME} (the "Service") is operated by {COMPANY} ("we", "us", "our"), with its registered office at {COMPANY_ADDRESS}.
              This Privacy Policy explains how we collect, use, store, and share information about you when you use {APP_NAME}.
              For privacy-related inquiries you can reach us at <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">2. What We Collect</h2>
            <p className="mb-3"><strong>Account information.</strong> Email address, name, and (for password accounts) a hashed password. If you sign in with Google, we also receive your name and profile picture.</p>
            <p className="mb-3"><strong>Content you create.</strong> Journal entries, coaching conversation messages, glimmers logged in the Echo Map, milestones, your Enneagram results, current identity, and Horizon goal.</p>
            <p className="mb-3"><strong>Usage information.</strong> Basic technical data such as your IP address, browser type, and timestamps of requests. We use this only to operate, secure, and improve the Service.</p>
            <p className="mb-3"><strong>Cookies.</strong> A single secure session cookie is used to keep you signed in. We do not use third-party advertising cookies or cross-site trackers.</p>
            <p><strong>Children.</strong> {APP_NAME} is not directed to children under 16, and we do not knowingly collect data from them.</p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide the Service, including your coaching conversations, journal, meditations, breathwork, and Echo Map.</li>
              <li>To personalize coaching responses based on your stated preferences and content you have shared with the AI coach.</li>
              <li>To send transactional emails such as account verification and password reset.</li>
              <li>To monitor and prevent fraud, abuse, and security incidents.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">4. Legal Bases (GDPR / UK GDPR users)</h2>
            <p>
              We process your personal data under one or more of the following legal bases: <strong>performance of a contract</strong> with you (delivering the Service you signed up for), <strong>your consent</strong> (where required, such as marketing), <strong>our legitimate interests</strong> (security, fraud prevention, product improvement), and <strong>compliance with legal obligations</strong>.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">5. AI Processing of Your Content</h2>
            <p>
              Your coaching conversations and journal entries are sent to a third-party large language model provider (currently OpenAI) so the AI coach can respond contextually. The provider processes this data on our behalf and is contractually prohibited from using it to train their general models on identifiable user content.
              You should not share personally identifying information, financial information, or content that could harm you if exposed. {APP_NAME} is not a substitute for licensed mental-health care.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">6. Sharing</h2>
            <p>We do not sell your personal information. We share data only with:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Service providers</strong> who help us operate the Service (cloud hosting, AI processing, email delivery, file storage). These are bound by data-processing agreements.</li>
              <li><strong>Authorities</strong> when required by valid legal process.</li>
              <li><strong>A successor entity</strong> if {COMPANY} is acquired or merges with another company. You will be notified in advance.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">7. International Transfers</h2>
            <p>
              We are based in Canada. Some of our service providers (cloud hosting, AI processing, email delivery) operate from the United States and other countries. By using {APP_NAME}, you understand that your information may be transferred to and processed in those jurisdictions, which may have different data-protection rules than your own. Where required, we rely on standard contractual clauses or equivalent safeguards.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">8. Data Retention</h2>
            <p>
              We retain your account and content for as long as your account is active. If you delete your account, we delete or anonymize your personal data within 30 days, except where retention is required for legal, accounting, fraud-prevention, or backup purposes (in which case the data is kept only as long as necessary).
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">9. Your Rights</h2>
            <p className="mb-4">
              Wherever you live, you have rights regarding your personal data. We honor these rights regardless of whether your country is specifically named below. To exercise any of them, email us at <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within 30 days (or sooner where law requires).
            </p>

            <p className="mt-5 mb-2 font-semibold">Universal rights</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Receive a portable copy of your data.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Lodge a complaint with your local data-protection authority.</li>
            </ul>

            <p className="mt-6 mb-2 font-semibold">🇨🇦 Canada — PIPEDA + Alberta PIPA</p>
            <p>
              You may access your personal information, request corrections, withdraw consent, and file a complaint with the Office of the Privacy Commissioner of Canada or the Office of the Information and Privacy Commissioner of Alberta. Quebec residents have additional rights under Law 25.
            </p>

            <p className="mt-5 mb-2 font-semibold">🇪🇺 European Union + 🇬🇧 United Kingdom — GDPR / UK GDPR</p>
            <p>
              You have the rights of access, rectification, erasure ("right to be forgotten"), restriction, portability, objection, and the right to withdraw consent at any time. You may also lodge a complaint with your local supervisory authority (e.g., the ICO in the UK).
            </p>

            <p className="mt-5 mb-2 font-semibold">🇺🇸 United States — State Privacy Laws</p>
            <p>
              Residents of California (CCPA/CPRA), Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), Utah (UCPA), Texas (TDPSA) and other states with comprehensive privacy laws may request to access, delete, correct, and opt out of certain processing. We do not sell personal information.
            </p>

            <p className="mt-5 mb-2 font-semibold">🇦🇺 Australia · 🇧🇷 Brazil · Other regions</p>
            <p>
              Australian residents have rights under the Privacy Act 1988. Brazilian residents have rights under LGPD. Residents of other countries with comparable privacy laws can exercise equivalent rights by emailing us. We do our best to honor whatever local right you have, even if it isn't explicitly listed here.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">10. International Users</h2>
            <p>
              {APP_NAME} is operated from Canada and is available to people worldwide. This Privacy Policy applies to you regardless of where you live. If your country or region has data-protection laws not listed above, those rights still apply to you, and we will work in good faith to honor them. If you believe we have failed to meet a legal obligation in your jurisdiction, please contact us first at <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> so we can resolve it directly.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">11. Data Processors We Use</h2>
            <p className="mb-3">
              We work with a small number of trusted third-party providers to deliver the Service. Each is bound by a data-processing agreement or equivalent commitment to safeguard your data.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>OpenAI</strong> (USA) — powers the AI coach. Content sent to the AI is processed under OpenAI's API Data Usage policy and is not used to train their general models.</li>
              <li><strong>MongoDB Atlas / hosting infrastructure</strong> (USA / EU regions) — stores your account, journal entries, glimmers, and coaching conversations.</li>
              <li><strong>Resend</strong> (USA) — sends transactional emails (welcome, verification, password reset).</li>
              <li><strong>Emergent</strong> (USA) — application hosting and deployment platform.</li>
              <li><strong>Google</strong> (USA) — only if you use "Sign in with Google" (we receive name, email, profile picture).</li>
            </ul>
            <p className="mt-3">
              We may update this list as our infrastructure evolves. Material changes will be reflected here and announced in-app.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">12. Security</h2>
            <p>
              We use industry-standard safeguards (TLS in transit, password hashing with bcrypt, secure session cookies, rate-limited authentication, principle-of-least-privilege access). No system is perfectly secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">13. Changes</h2>
            <p>
              We may update this policy from time to time. Material changes will be communicated through the Service or by email at least 14 days before they take effect. Continued use after the effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">14. Contact</h2>
            <p>
              Questions or requests: <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> · {COMPANY}, {COMPANY_ADDRESS}.
            </p>
          </section>
        </div>

        <div className="mystical-divider my-12 mx-auto max-w-[80px]" />
        <p className="text-center text-text-muted font-sans text-xs tracking-wider">
          <Link to="/terms" className="hover:text-dark">Terms of Service</Link>
          <span className="mx-3">·</span>
          <Link to="/login" className="hover:text-dark">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
